/**
 * An agent in the record panel: its two states, its capabilities, its most
 * recent tasks as rows, and Open to the agent page.
 */
import { useQuery } from '@tanstack/react-query';

import { getAgent } from '@optum/api-client/endpoints/agents';
import { listTasks } from '@optum/api-client/endpoints/tasks';

import { type RecordRef, useRecordPanel } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordOpen,
  RecordRelated,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { absoluteDate, relativeTime, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { IconTile, Typography } from '../../ui/finixt.js';
import { StatusBadge } from '../../ui/StatusBadge.js';

export function AgentRecord({ record }: { record: RecordRef }) {
  const panel = useRecordPanel();
  const id = record.id;
  const agent = useQuery({ queryKey: qk.agent(id), queryFn: () => getAgent(id) });
  const recent = useQuery({
    queryKey: qk.tasks({ agent_id: id, limit: 5 }),
    queryFn: () => listTasks({ agent_id: id, limit: 5 }),
  });

  return (
    <RecordAsync query={agent} record={record}>
      {(a) => (
        <RecordColumn>
          <RecordIdentity
            avatar={
              <IconTile
                icon="smart_toy"
                size={40}
                tint="var(--violet-tint)"
                color="var(--violet)"
              />
            }
            eyebrow={
              <>
                <Typography variant="eyebrow">Agent</Typography>
                <StatusBadge status={a.runtime_state} vocabulary="agentRuntime" />
                {a.lifecycle_state !== 'active' ? (
                  <StatusBadge status={a.lifecycle_state} vocabulary="agentLifecycle" />
                ) : null}
              </>
            }
            name={a.name}
            subtitle={a.description || undefined}
          />
          <RecordSection label="Facts">
            <RecordFacts
              items={[
                { label: 'ID', value: shortId(a.id) },
                { label: 'Capabilities', value: a.capabilities.join(', ') || '—' },
                { label: 'Created', value: absoluteDate(a.created_at) },
                { label: 'Updated', value: relativeTime(a.updated_at) },
              ]}
            />
          </RecordSection>
          {(recent.data ?? []).length > 0 ? (
            <RecordSection label="Recent tasks">
              <RecordRelated
                rows={(recent.data ?? []).map((t) => ({
                  icon: 'task_alt',
                  label: <span className="mono">{shortId(t.id)}</span>,
                  meta: `${t.status} · ${relativeTime(t.created_at)}`,
                  onClick: () => panel.push({ kind: 'task', id: t.id }),
                }))}
              />
            </RecordSection>
          ) : null}
          <RecordActions>
            <RecordOpen record={record} />
          </RecordActions>
        </RecordColumn>
      )}
    </RecordAsync>
  );
}
